import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import './style.css';
import { useDispatch, useSelector } from 'react-redux';
import { setCollapseLeftBar } from '../../features/organization/organizationSlice';

const ResizableWrapper = ({
  children,
  minWidth = 200,
  maxWidth = 600,
  defaultWidth = 273,
  onResize = () => {},
  ...props
}) => {
  const dispatch = useDispatch();
  const isLeftbarCollapsed = useSelector((state) => state.organization?.collapseLeftBar);

  // Get width from localStorage if available, otherwise use defaultWidth
  const storedWidth = useMemo(() => {
    const widthFromStorage = window.localStorage.getItem('onedoc_leftbarWidth');
    return widthFromStorage ? parseInt(widthFromStorage, 10) : defaultWidth;
  }, [defaultWidth, isLeftbarCollapsed]);

  const [width, setWidth] = useState(isLeftbarCollapsed ? 80 : storedWidth);

  // Refs for tracking resize state
  const isResizing = useRef(false);
  const resizeStartWidth = useRef(width);
  const resizableRef = useRef(null);

  const setLeftbarWidthInStyle = useCallback((width) => {
    document.documentElement.style.setProperty('--leftbar-width', `${width}px`);
  }, []);

  // Update width when collapse state changes
  useEffect(() => {
    if (isLeftbarCollapsed) {
      setWidth(80);
    } else if (!isResizing.current) {
      setWidth(storedWidth);
      setLeftbarWidthInStyle(storedWidth);
    }
  }, [isLeftbarCollapsed, storedWidth]);

  // Handle resize start
  const handleResizeStart = (e) => {
    // Prevent text selection during resize
    e.preventDefault();

    isResizing.current = true;
    resizeStartWidth.current = width;

    // If leftbar is collapsed, expand it when user starts resizing
    if (isLeftbarCollapsed) {
      dispatch(setCollapseLeftBar(false));
    }

    // Add a class to the body to disable transitions during resize
    document.body.classList.add('resizing-leftbar');

    // Close sidebar if it's open
    const sidebar = document.querySelector('.workspace-sidebar');
    if (sidebar && getComputedStyle(sidebar).display !== 'none') {
      // Find the isCollaspse setter in the Leftbar component
      const closeButton = sidebar.querySelector('.sidebar-content-right .anticon-double-left');
      if (closeButton) {
        closeButton.click();
      }
    }

    // Add event listeners for smooth resize
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle mouse move during resize (for smoother resizing)
  const handleMouseMove = (e) => {
    if (!isResizing.current) return;

    const container = resizableRef.current;
    if (!container) return;

    const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX));

    // Update DOM directly for smoother resizing
    const resizableContainer = container.querySelector('.resizable-container');
    if (resizableContainer) {
      resizableContainer.style.width = `${newWidth}px`;
      setWidth(newWidth);
      setLeftbarWidthInStyle(newWidth);
    }
  };

  // Handle mouse up to end resize
  const handleMouseUp = (e) => {
    if (!isResizing.current) return;

    // Clean up event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    // Calculate final width
    const container = resizableRef.current;
    if (!container) return;

    const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX));

    // Update state and Redux
    setWidth(newWidth);
    setLeftbarWidthInStyle(newWidth);
    onResize?.(newWidth);

    // Remove resizing class
    document.body.classList.remove('resizing-leftbar');
    isResizing.current = false;
  };

  // Handle resize from react-resizable (fallback)
  const handleResize = (e, { size }) => {
    if (!isResizing.current) return;

    // Ensure width stays within bounds
    const newWidth = Math.max(minWidth, Math.min(maxWidth, size.width));

    // Update state
    setWidth(newWidth);
    setLeftbarWidthInStyle(newWidth);
  };

  // Handle resize stop from react-resizable (fallback)
  const handleResizeStop = () => {
    if (!isResizing.current) return;

    // Remove the resizing class
    document.body.classList.remove('resizing-leftbar');
    isResizing.current = false;
    localStorage.setItem('onedoc_leftbarWidth', width);

    // Only dispatch to Redux if the width actually changed
    if (width !== resizeStartWidth.current) {
      onResize?.(width);
    }
  };

  return (
    <div ref={resizableRef} className="resizable-wrapper">
      <Resizable
        width={width}
        height={0} // Height is controlled by your content
        minConstraints={[minWidth, 0]}
        maxConstraints={[maxWidth, 0]}
        onResize={handleResize}
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeStop}
        resizeHandles={['e']} // East (right) handle only
        handle={
          <div className="resize-handle" onMouseDown={handleResizeStart}>
            <div className="resize-handle-inner"></div>
          </div>
        }
        {...props}
      >
        <div style={{ width: `${width}px`, background: '#fff' }} className="resizable-container">
          {children}
        </div>
      </Resizable>
    </div>
  );
};

export default ResizableWrapper;
